/**
 * NotesTab — Staycation segment notes with 3 sections:
 *   1. Manual notes (project_notes table) — CRUD inline
 *   2. Field notes (voicenotes matched on production_slug)
 *   3. Refs notes (voicenotes that reference this slug via production_refs)
 *   4. Outlines (production_outlines for the season)
 */
import { useState } from 'react';
import {
  Box, Typography, Paper, IconButton, Stack, TextField, Button, Accordion,
  AccordionSummary, AccordionDetails, Chip, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactMarkdown from 'react-markdown';
import {
  useProjectFull,
  useProjectProduction,
  useCreateProjectNote,
  useUpdateProjectNote,
  useDeleteProjectNote,
} from '../../hooks/useProjects';

export default function NotesTab({ projectId, readOnly = false }) {
  const { data: full } = useProjectFull(projectId);
  const { data: production, isLoading: prodLoading } = useProjectProduction(projectId);
  const manualNotes = full?.notes || [];

  return (
    <Box>
      {/* Manual notes */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Manual notes ({manualNotes.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ManualNotes notes={manualNotes} projectId={projectId} readOnly={readOnly} />
        </AccordionDetails>
      </Accordion>

      {/* Field notes (voicenotes by slug) */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Field notes ({production?.voicenotes?.length || 0})
            {production?.slug && <Chip size="small" label={production.slug} sx={{ ml: 1 }} />}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {prodLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
          {!prodLoading && !production?.slug && (
            <Typography variant="body2" color="text.secondary">
              No production_slug set on this segment. Add one in Edit to link voicenotes.
            </Typography>
          )}
          {(production?.voicenotes || []).map((vn) => <VoicenoteCard key={vn.id} vn={vn} />)}
        </AccordionDetails>
      </Accordion>

      {/* Refs (voicenotes that mention this slug via production_refs) */}
      {(production?.refs_voicenotes?.length > 0) && (
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Referenced in other notes ({production.refs_voicenotes.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {production.refs_voicenotes.map((vn) => <VoicenoteCard key={vn.id} vn={vn} compact />)}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Production outlines */}
      {(production?.outlines?.length > 0) && (
        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Run of Show ({production.outlines.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {production.outlines.map((o) => (
              <Paper key={o.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" label={o.outline_type} />
                  <Typography variant="caption" color="text.secondary">
                    {o.updated_at ? new Date(o.updated_at).toLocaleString() : ''}
                  </Typography>
                </Stack>
                {o.title && <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{o.title}</Typography>}
                {o.body && (
                  <Box sx={{ fontSize: 13, '& p': { my: 0.5 } }}>
                    <ReactMarkdown>{String(o.body).slice(0, 4000)}</ReactMarkdown>
                  </Box>
                )}
              </Paper>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

function ManualNotes({ notes, projectId, readOnly }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const createMut = useCreateProjectNote();
  const updateMut = useUpdateProjectNote();
  const deleteMut = useDeleteProjectNote();

  return (
    <Box>
      {!readOnly && !adding && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mb: 1 }}>
          Add note
        </Button>
      )}
      {adding && (
        <NoteEditor
          onSave={async ({ title, body }) => {
            await createMut.mutateAsync({ project_id: projectId, title, body });
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
          busy={createMut.isPending}
        />
      )}
      {notes.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary">No manual notes yet.</Typography>
      )}
      {notes.map((n) => (
        <Paper key={n.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          {editingId === n.id ? (
            <NoteEditor
              initial={n}
              onSave={async ({ title, body }) => {
                await updateMut.mutateAsync({ id: n.id, title, body });
                setEditingId(null);
              }}
              onCancel={() => setEditingId(null)}
              busy={updateMut.isPending}
            />
          ) : (
            <>
              <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                {n.title && <Typography variant="subtitle2" sx={{ flex: 1 }}>{n.title}</Typography>}
                <Typography variant="caption" color="text.secondary">
                  {n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}
                </Typography>
                {!readOnly && (
                  <>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditingId(n.id)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => { if (confirm('Delete note?')) deleteMut.mutate(n.id); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
              <Box sx={{ fontSize: 14, '& p': { my: 0.5 } }}>
                <ReactMarkdown>{n.body || ''}</ReactMarkdown>
              </Box>
            </>
          )}
        </Paper>
      ))}
    </Box>
  );
}

function NoteEditor({ initial, onSave, onCancel, busy }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  return (
    <Stack spacing={1}>
      <TextField size="small" label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
      <TextField size="small" label="Body (markdown supported)" value={body} onChange={(e) => setBody(e.target.value)} fullWidth multiline rows={5} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button size="small" variant="contained" onClick={() => onSave({ title, body })} disabled={busy || !body.trim()}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

function VoicenoteCard({ vn, compact = false }) {
  const refs = vn.production_refs && typeof vn.production_refs === 'object'
    ? Object.keys(vn.production_refs)
    : [];
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{vn.title || '(untitled voicenote)'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {vn.created_at ? new Date(vn.created_at).toLocaleDateString() : ''}
        </Typography>
      </Stack>
      {refs.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          {refs.slice(0, 6).map((r) => <Chip key={r} size="small" label={r} />)}
        </Stack>
      )}
      {vn.transcript && (
        <Typography variant="body2" sx={{
          color: 'text.secondary',
          whiteSpace: 'pre-wrap',
          maxHeight: compact ? 80 : 240,
          overflow: 'auto',
          fontSize: 13,
        }}>
          {vn.transcript}
        </Typography>
      )}
    </Paper>
  );
}
